import { useState } from 'react'
import { Box, Divider, Input, TextField, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { grey } from '@mui/material/colors'
import { toPaletteColor } from '@/common/mui-utils'
import { coloredSubmitButtonSx } from '@/core/theme/style-variants'
import { SubmitButton } from '@/common/components/SubmitButton'
import { PropertyForm } from '../property-forms/PropertyForm'
import { ObjectFormContext } from './ObjectFormContext'
import type { IObjectFormController } from './IObjectFormController'
import type { PropertyValue } from '../services/ObjectsService'

interface ObjectFormPageProps {
  label?: string
  controller: IObjectFormController
}

const nameWidthPx = 130
const metaNameWidthPx = 150
const metaInputMaxWidth = 110

export function ObjectFormPage({ label, controller }: ObjectFormPageProps) {
  const metaColor = toPaletteColor(grey)

  const nameId = controller.namePropDef().id
  const descrId = controller.descrPropDef().id

  const [name, setName] = useState(() => String(controller.getPropValue(nameId) ?? ''))
  const [descr, setDescr] = useState(() => String(controller.getPropValue(descrId) ?? ''))
  const [error, setError] = useState<string | undefined>()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const customPropDefs = controller.getCustomPropDefs()
  const metaPropDefs = controller.getMetaPropDefs()

  function onNameChange(value: string) {
    setName(value)
    controller.onPropValueChange(nameId, value)
  }

  function onDescrChange(value: string) {
    setDescr(value)
    controller.onPropValueChange(descrId, value)
  }

  async function onSubmit() {
    if (!controller.onSubmit) return
    setIsSubmitting(true)
    const err = await controller.onSubmit()
    if (err) setError(err)
    setIsSubmitting(false)
  }

  return (
    <ObjectFormContext.Provider value={controller}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          p: 2,
          overflow: 'hidden auto',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          {label ? <Typography variant="body1">{label}</Typography> : <Box />}
          <CloseIcon
            sx={{
              fontSize: 28,
              cursor: 'pointer',
              color: 'error.main',
              '&:hover': { color: 'error.dark', stroke: 'currentColor', strokeWidth: 1 },
            }}
            onClick={() => controller.onClose()}
          />
        </Box>

        <Input
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Name"
          disableUnderline
          autoFocus
          sx={{
            fontSize: '2rem',
            borderBottom: '4px solid',
            borderColor: 'primary.main',
            width: 'fit-content',
            mb: 2,
            '& input': {
              width: `${Math.max(name.length, 4) + 1}ch`,
              padding: 0,
              lineHeight: 1,
              height: 'auto',
            },
          }}
        />

        {customPropDefs.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
            {customPropDefs.map((propDef) => (
              <PropertyForm
                key={propDef.id}
                propDef={propDef}
                initialValue={controller.getPropValue(propDef.id)}
                nameWidthPx={nameWidthPx}
              />
            ))}
          </Box>
        )}

        <TextField
          label="Description"
          value={descr}
          onChange={(e) => onDescrChange(e.target.value)}
          multiline
          minRows={3}
          maxRows={8}
          fullWidth
          sx={{ mb: 2 }}
        />

        {metaPropDefs.length > 0 && (
          <>
            <Divider sx={{ borderColor: metaColor.light, borderBottomWidth: 2, my: 1 }} />
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              Meta Properties:
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              {metaPropDefs.map((propDef) => (
                <PropertyForm
                  key={propDef.id}
                  propDef={propDef}
                  initialValue={controller.getPropValue(propDef.id)}
                  nameWidthPx={metaNameWidthPx}
                  inputMaxWidth={metaInputMaxWidth}
                  readonly
                />
              ))}
            </Box>
          </>
        )}

        {controller.onSubmit && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2 }}>
            {error && (
              <Typography fontSize={12} sx={{ color: 'error.light', flex: 1 }}>
                {error}
              </Typography>
            )}
            <SubmitButton
              isSubmitting={isSubmitting}
              onClick={() => void onSubmit()}
              sx={coloredSubmitButtonSx}
            >
              Create
            </SubmitButton>
          </Box>
        )}
      </Box>
    </ObjectFormContext.Provider>
  )
}
